import {
  useCreatePrivateCareSite,
  useMyCareSites,
} from "@/api/careSites/useCareSites";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { routes } from "@/constants/routes";
import {
  CARE_SITE_KIND_LABEL,
  labelOf,
  LOADING_LABEL,
} from "@/constants/uiLabels";
import { getShadowStyle } from "@/theme/shadowStyle";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { StatusChip } from "@/ui/components/StatusChip";
import { viewWhileLoading } from "@/ui/loadingState";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { Text, View } from "react-native";

/**
 * Meine Pflegestellen – Liste + private Site anlegen.
 */
export default function CareSitesIndexScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { sites, loading, error, refetch } = useMyCareSites();
  const { organizations, loading: orgsLoading } = useMyOrganizations();
  const createSite = useCreatePrivateCareSite();
  const shadow = getShadowStyle(theme.cardShadow);

  const [name, setName] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const orgId = useMemo(() => organizations[0]?.id ?? null, [organizations]);

  const onCreate = async () => {
    setMessage(null);
    if (!orgId) {
      setMessage("Keine aktive Organisation.");
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage("Name erforderlich.");
      return;
    }
    try {
      const site = await createSite.mutateAsync({
        organizationId: orgId,
        name: trimmed,
      });
      setName("");
      await refetch();
      router.push(routes.myCareSite(site.id) as any);
    } catch (e: any) {
      setMessage(e?.message ?? "Anlegen fehlgeschlagen.");
    }
  };

  return (
    <Screen
      title="Meine Pflegestellen"
      scroll
      contentStyle={{ paddingBottom: theme.space.xxl }}
    >
      <View style={{ gap: theme.space.lg }}>
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.small,
          }}
        >
          Übersicht deiner privaten und organisatorischen Pflegestellen.
        </Text>

        <View
          style={{
            backgroundColor: theme.color.surface,
            borderRadius: theme.radius.md,
            borderWidth: 1,
            borderColor: theme.color.border,
            overflow: "hidden",
            ...shadow,
          }}
        >
          <View
            style={{
              paddingHorizontal: theme.space.md,
              paddingVertical: theme.space.sm,
              borderBottomWidth: 1,
              borderBottomColor: theme.color.border,
              backgroundColor: theme.color.bgStrong,
            }}
          >
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              Pflegestellen
              {sites.length ? ` · ${sites.length}` : ""}
            </Text>
          </View>
          {loading ? (
            <Text
              style={{
                color: theme.color.muted,
                padding: theme.space.md,
                fontSize: theme.fontSize.small,
              }}
            >
              Laden…
            </Text>
          ) : sites.length === 0 ? (
            <View style={{ paddingHorizontal: theme.space.md }}>
              <EmptyState
                title="Noch keine Pflegestelle."
                hint="Lege unten eine private Pflegestelle an."
              />
            </View>
          ) : (
            sites.map((site) => (
              <DenseRow
                key={site.id}
                title={site.name}
                meta={
                  site.ownership === "private" ? "Privat" : "Organisation"
                }
                trailing={
                  <StatusChip
                    label={labelOf(CARE_SITE_KIND_LABEL, site.kind)}
                    tone="neutral"
                    size="sm"
                  />
                }
                onPress={() => router.push(routes.myCareSite(site.id) as any)}
              />
            ))
          )}
          {error ? (
            <Text
              style={{
                color: theme.color.danger,
                padding: theme.space.md,
                fontSize: theme.fontSize.small,
              }}
            >
              {(error as Error).message}
            </Text>
          ) : null}
        </View>

        <SectionCard title="Private Pflegestelle anlegen">
          {viewWhileLoading(orgsLoading, !orgId) === "loading" ? (
            <Text style={{ color: theme.color.muted }}>{LOADING_LABEL}</Text>
          ) : !orgId ? (
            <EmptyState
              title="Keine aktive Organisationsmitgliedschaft."
              hint="Ohne Verein kannst du keine Pflegestelle anlegen."
            />
          ) : (
            <View style={{ gap: theme.space.md }}>
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: theme.fontSize.small,
                }}
              >
                Organisation: {organizations[0]?.name}
              </Text>
              <FloatingLabelInput
                label="Name"
                value={name}
                onChangeText={setName}
              />
              <AppButton
                onPress={onCreate}
                disabled={createSite.isPending}
              >
                {createSite.isPending ? "Anlegen…" : "Anlegen"}
              </AppButton>
              {message ? (
                <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
                  {message}
                </Text>
              ) : null}
            </View>
          )}
        </SectionCard>
      </View>
    </Screen>
  );
}
